/**
 * Form layer for product linker.
 * Responsible for DOM elements getter/setter.
 */
import { Roles } from "../../constants/roles";

import { FormBase } from "../../util/form-base-form";

class ProductLinkerForm extends FormBase {
    public static getRoots(root: ParentNode = document): HTMLElement[] {
        return new ProductLinkerForm(document.documentElement).queryElements(
            root,
            `[data-role="${Roles.POST_PRODUCT_LINKER}"], [data-role="${Roles.POST_PRODUCT_LINKER_VIEW}"]`,
            HTMLElement,
        );
    }

    public root: HTMLElement;
    public hiddenInput: HTMLInputElement | null;
    public preview: HTMLElement | null;
    public searchInput: HTMLInputElement | null;
    public openBtn: HTMLButtonElement | null;
    public clearBtn: HTMLButtonElement | null;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
        this.hiddenInput = this.getInput(root, `[data-role="${Roles.POST_PRODUCT_ID}"]`);
        this.preview = this.queryElement(root, `[data-role="${Roles.POST_PRODUCT_PREVIEW}"]`, HTMLElement);
        this.searchInput = this.queryElement(
            root,
            `[data-role="${Roles.POST_PRODUCT_SEARCH_INPUT}"]`,
            HTMLInputElement,
        );
        this.openBtn = this.getButton(root, `[data-role="${Roles.POST_PRODUCT_OPEN}"]`);
        this.clearBtn = this.getButton(root, `[data-role="${Roles.POST_PRODUCT_CLEAR}"]`);
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public getOpenButton(): HTMLButtonElement | null {
        return this.openBtn;
    }

    public getClearButton(): HTMLButtonElement | null {
        return this.clearBtn;
    }

    public getSearchInput(): HTMLInputElement | null {
        return this.searchInput;
    }

    public getPreviewElement(): HTMLElement | null {
        return this.preview;
    }

    public getProductId(): number | null {
        const rawProductId = String(this.hiddenInput?.value || "").trim();
        const productId = Number(rawProductId);
        return Number.isInteger(productId) && productId > 0 ? productId : null;
    }

    public setProductId(id: number | string | null): void {
        const value = String(id ?? "");
        if (this.hiddenInput) {
            this.hiddenInput.value = value;
        }

        // Review create mode (no product/brand pre-supplied — e.g. the navbar
        // "Write a Review" entry point) renders this linker alongside a separate
        // review_data[post_product_id] hidden field (post-form-fields.blade.php's
        // Review Questions Section), which is what the submission actually reads
        // for the review's product linkage. Keep it in sync with this linker's
        // own selection. No-op (field doesn't exist) for every other case — edit
        // mode, non-review boards, product/brand-page-originated reviews.
        const reviewDataInput = this.getReviewDataProductIdInput();
        if (reviewDataInput) {
            reviewDataInput.value = value;
        }
    }

    private getReviewDataProductIdInput(): HTMLInputElement | null {
        const form = this.closest(this.root, "form", HTMLFormElement);
        if (!form) return null;
        return this.getInput(form, 'input[name="review_data[post_product_id]"]');
    }

    public focusSearchInput(): void {
        this.searchInput?.focus();
    }

    public clearSearchInput(): void {
        if (this.searchInput) {
            this.searchInput.value = "";
        }
    }

    public getClosestAnchor(root: Event | EventTarget | Element | null | undefined): HTMLAnchorElement | null {
        return this.closest(root, "a", HTMLAnchorElement);
    }

    public getClosestCardBody(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".card-body", HTMLElement);
    }

    public getClosestPostCard(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-role="post-card"]', HTMLElement);
    }

    public getThumb(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PRODUCT_THUMB}"]`, HTMLElement);
    }

    public getThumbImage(root: ParentNode): HTMLImageElement | null {
        return this.queryElement(root, "img", HTMLImageElement);
    }

    public getThumbPlaceholder(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.POST_PRODUCT_THUMB_PLACEHOLDER}"]`, HTMLElement);
    }

    /** Whether the category icon sprite (rendered once in layouts/app.blade.php) defines this <symbol> id. */
    public hasCategoryIconSymbol(symbolId: string): boolean {
        // <symbol> is an SVGElement, not an HTMLElement — DomQuery.queryElement()
        // defaults to accepting only HTMLElement/HTMLFormElement, so the expected
        // class must be passed explicitly or a real match is silently rejected.
        return this.queryElement(document, `#${symbolId}`, SVGElement) !== null;
    }
}

export { ProductLinkerForm };
